package com.nonopn.skydome.common.app.pages.dashboard

import com.nonopn.skydome.common.app.pages.documents.projectUuid
import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryItem
import kotlin.math.PI
import kotlin.math.asin
import kotlin.math.cos
import kotlin.math.roundToInt
import kotlin.math.sin
import kotlin.math.sqrt

// Pure helpers of the dashboard map pop-ups (probe and LPS Manager site). Only data already loaded by the
// dashboard is used. No UI nor network here.

/** Radius around a probe in which a site is considered as covered. */
const val ProbeCoverageKm = 100.0

private const val EarthRadiusKm = 6371.0
private const val LastDaysCount = 7

/** A point of the map: a probe or a site. */
data class MapPlace(
    val uuid: String,
    val name: String,
    val lat: Double,
    val lon: Double,
)

/** Which kind of point is open in the pop-up. */
enum class MapPointKind { PROBE, SITE }

data class MapPointRef(val kind: MapPointKind, val uuid: String)

/** Content of a probe pop-up. A null count is not available and must not be displayed. */
data class ProbePopup(
    val uuid: String,
    val name: String,
    val today: Int?,
    val last7Days: Int?,
    val last30Days: Int?,
    val sitesCovered: Int,
)

/** Probe covering a site and the distance to it. */
data class ProbeDistance(val probe: MapPlace, val km: Int)

/** Content of a site pop-up. A null value is not available and must not be displayed. */
data class SitePopup(
    val uuid: String,
    val name: String,
    val createdAtMillis: Long?,
    val coveringProbe: ProbeDistance?,
    // False while the history of documents is not known yet, so that "no document" is never guessed
    val hasHistory: Boolean,
    val lastDocument: DocumentHistoryItem?,
)

data class CivilDate(val year: Int, val month: Int, val day: Int)

/** Days since 1970-01-01 of a civil date (proleptic Gregorian calendar). */
fun epochDay(year: Int, month: Int, day: Int): Long {
    val y = if (month <= 2) year - 1 else year
    val era = (if (y >= 0) y else y - 399) / 400
    val yearOfEra = y - era * 400
    val monthIndex = (month + 9) % 12
    val dayOfYear = (153 * monthIndex + 2) / 5 + day - 1
    val dayOfEra = yearOfEra * 365 + yearOfEra / 4 - yearOfEra / 100 + dayOfYear
    return era.toLong() * 146097 + dayOfEra - 719468
}

/**
 * Impacts of the last 7 days (today included) from the daily counts of the 30 days report, keyed `DD/MM`.
 * When today is missing from the daily counts, the count of today is added. Null when the daily counts are
 * not available.
 */
fun impactsLast7Days(daily: Map<String, Int>?, today: CivilDate, todayCount: Int?): Int? {
    if (daily == null) return null
    val todayEpoch = epochDay(today.year, today.month, today.day)
    var sum = 0
    var hasToday = false
    daily.forEach { (key, count) ->
        val parts = key.split("/")
        val day = parts.getOrNull(0)?.trim()?.toIntOrNull() ?: return@forEach
        val month = parts.getOrNull(1)?.trim()?.toIntOrNull() ?: return@forEach
        // A month after the current one belongs to the previous year (December / January crossover)
        val year = if (month > today.month) today.year - 1 else today.year
        val delta = todayEpoch - epochDay(year, month, day)
        if (delta in 0 until LastDaysCount) {
            sum += count
            if (delta == 0L) hasToday = true
        }
    }
    if (!hasToday && todayCount != null) sum += todayCount
    return sum
}

/** Great-circle distance in kilometres between two WGS84 points. */
fun distanceKm(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
    val dLat = (lat2 - lat1) * PI / 180.0
    val dLon = (lon2 - lon1) * PI / 180.0
    val a = sin(dLat / 2) * sin(dLat / 2) +
        cos(lat1 * PI / 180.0) * cos(lat2 * PI / 180.0) * sin(dLon / 2) * sin(dLon / 2)
    return 2 * EarthRadiusKm * asin(sqrt(a.coerceIn(0.0, 1.0)))
}

/** Number of sites in the coverage radius of a probe. */
fun sitesCovered(probe: MapPlace, sites: List<MapPlace>, radiusKm: Double = ProbeCoverageKm): Int =
    sites.count { distanceKm(probe.lat, probe.lon, it.lat, it.lon) <= radiusKm }

/** Nearest probe covering the site, null when no probe is in the coverage radius. */
fun nearestCoveringProbe(
    site: MapPlace,
    probes: List<MapPlace>,
    radiusKm: Double = ProbeCoverageKm,
): ProbeDistance? = probes
    .map { it to distanceKm(it.lat, it.lon, site.lat, site.lon) }
    .filter { (_, km) -> km <= radiusKm }
    .minByOrNull { (_, km) -> km }
    ?.let { (probe, km) -> ProbeDistance(probe, km.roundToInt()) }

/** Most recent document (any type) produced for a site, null when none. */
fun latestDocumentOf(history: List<DocumentHistoryItem>, projectUuid: String): DocumentHistoryItem? = history
    .filter { it.projectUuid() == projectUuid }
    .maxByOrNull { it.createdAtSecondsUtc }

fun buildProbePopup(
    probe: MapPlace,
    sites: List<MapPlace>,
    today: Int?,
    last7Days: Int?,
    last30Days: Int?,
) = ProbePopup(
    uuid = probe.uuid,
    name = probe.name,
    today = today,
    last7Days = last7Days,
    last30Days = last30Days,
    sitesCovered = sitesCovered(probe, sites),
)

fun buildSitePopup(
    site: MapPlace,
    createdAtMillis: Long?,
    probes: List<MapPlace>,
    history: List<DocumentHistoryItem>,
    historyReady: Boolean,
) = SitePopup(
    uuid = site.uuid,
    name = site.name,
    createdAtMillis = createdAtMillis?.takeIf { it > 0L },
    coveringProbe = nearestCoveringProbe(site, probes),
    hasHistory = historyReady,
    lastDocument = if (historyReady) latestDocumentOf(history, site.uuid) else null,
)
